/**
 * The day-14 downgrade takeover (PPL-358) — the most important screen in the
 * monetization program, shown full-screen on first open after the trial lapses,
 * exactly once per user (server-side seen-marker; a second device fetching
 * `seen: true` renders nothing, and the server's `eligible` gate exits for a
 * user who paid on another device while this tab held a stale snapshot).
 *
 * Three beats, in order: the receipt → the choice → the reassurance. Two
 * variants replace them when the theatre would be dishonest: at or under the
 * free limit there is no tension to perform (the receipt and the reassurance
 * line, nothing else), and an account f8 never got to remember anyone on gets
 * a beat that sells marking people, not memory.
 *
 * Rules the design locks:
 *  - Numbers are measured, never rounded, never invented. A null count drops
 *    its beat rather than rendering a zero nobody measured, and the trial's
 *    length is spoken via trialSpanLabel — never a hard-coded "two weeks".
 *  - NOTHING is pre-selected. The user taps each person to remember; unmarked
 *    cards visibly flip to paused. One explicit commit — an abandoned flow changes nothing.
 *  - The upgrade CTA sits ABOVE the picker, the escape hatch from a choice
 *    they do not want to make.
 *  - preserved / archived / paused — never lost, never deleted. Exit lands on
 *    the normal app, not the premium page.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MonoAvatar from '../MonoAvatar';
import { Moon } from './glyphs';
import { RT } from './tokens';
import { capture } from '../../services/posthog';
import { MonetizationEvents, PaywallSource } from '../../services/monetizationEvents';
import { getPricing, DEFAULT_USD_MONTHLY } from '../../services/premiumService';
import { fetchRememberQuota } from '../../services/rememberService';
import {
  commitKeepSelection,
  fetchTrialSummaryOnce,
  trialSpanLabel,
  type CommitResult,
  type TrialCandidate,
  type TrialSummary,
} from '../../services/trialService';

// The product's own ground — the overlay must not depend on a page stylesheet
// being loaded, so the two ambience values live here rather than in CSS vars.
const BG = '#0e0712';
const GLOW =
  'radial-gradient(500px 340px at 85% -5%, rgba(217,70,239,0.13), transparent 60%), ' +
  'radial-gradient(420px 300px at -10% 30%, rgba(124,58,237,0.11), transparent 60%)';
// One gradient, matching the upgrade sheet and lapse banner these hand off to.
const GRAD = `linear-gradient(135deg, ${RT.magenta2}, ${RT.violetDeep})`;

function GradTxt({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: GRAD,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }}
    >
      {children}
    </span>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, color: RT.magenta2, fontWeight: 700,
      letterSpacing: '0.12em', marginBottom: 10,
    }}>
      {children}
    </div>
  );
}

function CheckIcon({ size = 11, color = '#fff', width = 3 }: { size?: number; color?: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 12l5 5L20 7" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Stat({ n, plus, label }: { n: number; plus?: boolean; label: string }) {
  return (
    <div>
      <div style={{
        fontSize: 'clamp(44px, 7vw, 58px)', fontWeight: 700, letterSpacing: '-0.045em',
        lineHeight: 1, fontVariantNumeric: 'tabular-nums',
      }}>
        <GradTxt>{n}{plus ? '+' : ''}</GradTxt>
      </div>
      <div style={{ color: RT.muted, fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>{label}</div>
    </div>
  );
}

function GhostBtn({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none', cursor: 'pointer', padding: '13px 24px',
        borderRadius: 13, background: 'rgba(255,255,255,0.05)', border: `1px solid ${RT.border}`,
        color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

function CtaBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none', cursor: 'pointer', padding: '12px 22px', borderRadius: 13,
        background: GRAD, border: 'none',
        color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
        boxShadow: '0 12px 32px rgba(217,70,239,0.35)', whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

const shortDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toLowerCase();

/**
 * "birthday sep 9" when the next occurrence lands within 30 days (today
 * included), else null. Parts outside a real calendar (a rolled-over 31/02, a
 * month of 13) render nothing rather than a date JS invented.
 */
function upcomingBirthday(c: TrialCandidate, now = new Date()): string | null {
  const day = Number(c.dobDay);
  const month = Number(c.dobMonth);
  if (!Number.isInteger(day) || !Number.isInteger(month)) return null;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(now.getFullYear(), month - 1, day);
  if (next.getMonth() !== month - 1) return null; // rolled over — not a real date
  if (next.getTime() < today.getTime()) {
    next = new Date(now.getFullYear() + 1, month - 1, day);
    if (next.getMonth() !== month - 1) return null;
  }
  const days = (next.getTime() - today.getTime()) / (24 * 60 * 60 * 1000);
  if (days > 30) return null;
  return `birthday ${shortDate(next)}`;
}

/* ------------------------------------------------------------------ *
 * Beats
 * ------------------------------------------------------------------ */

function BeatReceipt({ s, onNext }: { s: TrialSummary; onNext: () => void }) {
  const span = trialSpanLabel(s) ?? 'your trial';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%', padding: '72px 0 56px' }}>
      <Kicker>your trial, in your numbers</Kicker>
      <h1 style={{ margin: 0, fontSize: 'clamp(30px, 5vw, 42px)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.04, color: '#fff' }}>
        in {span}, f8 learned
      </h1>
      <div style={{ display: 'flex', gap: 48, marginTop: 36, flexWrap: 'wrap' }}>
        <Stat n={s.learned as number} plus={s.truncated} label={`thing${s.learned === 1 ? '' : 's'} about the people in your life`} />
        <Stat n={s.people as number} plus={s.truncated} label={`${s.people === 1 ? 'person' : 'people'} it remembered`} />
        {s.prepped !== null && (
          <Stat n={s.prepped} label={`meeting${s.prepped === 1 ? '' : 's'} it prepped you for`} />
        )}
      </div>
      <p style={{ margin: '36px 0 0', color: RT.muted, fontSize: 14.5, lineHeight: 1.6, maxWidth: 460 }}>
        today the trial ended. everything f8 learned is preserved — every note, every person, every story.
      </p>
      <div style={{ marginTop: 28 }}><GhostBtn label="so what changes?" onClick={onNext} /></div>
    </div>
  );
}

function BeatZero({ s, onExit, onSync }: { s: TrialSummary; onExit: () => void; onSync: () => void }) {
  const span = trialSpanLabel(s);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%', padding: '72px 0 56px' }}>
      <Kicker>your trial ended today</Kicker>
      <h1 style={{ margin: 0, fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.06, color: '#fff' }}>
        f8 can only remember<br />the ppl you <GradTxt>mark.</GradTxt>
      </h1>
      <p style={{ margin: '20px 0 0', color: RT.muted, fontSize: 14.5, lineHeight: 1.6, maxWidth: 440 }}>
        {span ? `these ${span}, ` : 'during the trial, '}nobody was marked for f8 to remember — so
        nothing changes today. mark your ppl and f8 starts remembering them for real. free covers it.
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 30, flexWrap: 'wrap' }}>
        <CtaBtn label="mark your ppl" onClick={onSync} />
        <GhostBtn label="not now" onClick={onExit} />
      </div>
    </div>
  );
}

function BeatFew({ s, onExit }: { s: TrialSummary; onExit: () => void }) {
  const n = s.candidates.length;
  const span = trialSpanLabel(s) ?? 'the trial';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%', padding: '72px 0 56px' }}>
      <Kicker>your trial, in your numbers</Kicker>
      <h1 style={{ margin: 0, fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.05, color: '#fff' }}>
        {s.learned !== null ? (
          <>in {span}, f8 learned <GradTxt>{s.learned}{s.truncated ? '+' : ''} thing{s.learned === 1 ? '' : 's'}</GradTxt> about your {n} ppl.</>
        ) : (
          <>nothing changes for you.</>
        )}
      </h1>
      <p style={{ margin: '22px 0 0', color: RT.muted, fontSize: 14.5, lineHeight: 1.6, maxWidth: 440 }}>
        the trial ended today, and nothing is taken away: you&rsquo;ve marked fewer ppl than free
        remembers, so f8 remembers all {n}, same as yesterday.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 22 }}>
        {s.candidates.map((p) => (
          <span key={p.personId} style={{ marginRight: -8 }}><MonoAvatar name={p.name} size={32} /></span>
        ))}
        <span style={{ marginLeft: 14, color: RT.muted, fontSize: 12.5 }}>
          all {n} — still remembered
        </span>
      </div>
      <div style={{ marginTop: 30 }}><GhostBtn label="back to f8" onClick={onExit} /></div>
    </div>
  );
}

interface CardMeta {
  candidate: TrialCandidate;
  /** "23 things · dinner · last tue" — precomputed once; dates never change mid-flow. */
  meta: string;
  birthday: string | null;
}

const KeepCard = React.memo(function KeepCard({ row, kept, full, onToggle }: {
  row: CardMeta; kept: boolean; full: boolean; onToggle: (id: string) => void;
}) {
  const p = row.candidate;
  const paused = !kept && full;
  return (
    <div
      onClick={() => onToggle(p.personId)}
      role="checkbox"
      aria-checked={kept}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(p.personId); } }}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 14,
        cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
        background: kept ? RT.magentaTint : paused ? 'rgba(255,255,255,0.015)' : RT.surface,
        border: `1px solid ${kept ? RT.magentaTintBorder : RT.border}`,
      }}
    >
      <span style={{ filter: paused ? 'saturate(0.35)' : 'none', opacity: paused ? 0.75 : 1 }}>
        <MonoAvatar name={p.name} size={36} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          color: paused ? RT.muted : '#fff', fontSize: 13.5, fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {p.name}
        </div>
        <div style={{
          color: RT.dim, fontSize: 11, marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {row.meta}
          {row.birthday ? <span style={{ color: paused ? RT.dim : RT.magenta }}> · {row.birthday}</span> : null}
        </div>
      </div>
      {kept ? (
        <span style={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          background: GRAD,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(217,70,239,0.4)',
        }}>
          <CheckIcon />
        </span>
      ) : paused ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: RT.gold, fontSize: 10.5, fontWeight: 600, flexShrink: 0 }}>
          <Moon size={11} /><span>pauses</span>
        </span>
      ) : (
        <span style={{ width: 22, height: 22, borderRadius: '50%', border: `1.5px solid ${RT.borderStrong}`, flexShrink: 0 }} />
      )}
    </div>
  );
});

/**
 * True only on the calendar day the trial lapsed. The app reads this from
 * trialService (`endedToday`); the web trial service does not export it yet, so
 * the same day-comparison lives here until the two are reconciled.
 */
function endedToday(s: Pick<TrialSummary, 'trialEndsAt'>, now: Date = new Date()): boolean {
  const e = s.trialEndsAt;
  if (!e) return false;
  return e.getFullYear() === now.getFullYear()
    && e.getMonth() === now.getMonth()
    && e.getDate() === now.getDate();
}

function BeatChoice({ s, rows, monthly, kept, onToggle, onCommit, onUpgrade, committing, commitError }: {
  s: TrialSummary;
  rows: CardMeta[];
  monthly: number;
  kept: Set<string>;
  onToggle: (id: string) => void;
  onCommit: () => void;
  onUpgrade: () => void;
  committing: boolean;
  commitError: boolean;
}) {
  const lim = s.freeLimit as number;
  const n = kept.size;
  const full = n >= lim;
  const canCommit = n > 0 && !committing;
  const today = endedToday(s);
  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', padding: '56px 0 0' }}>
      <h1 style={{ margin: 0, fontSize: 'clamp(26px, 4.5vw, 36px)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.08, color: '#fff' }}>
        {today ? 'your trial ended today.' : 'your trial has ended.'} choose the {lim} ppl f8 remembers.
      </h1>
      <div style={{
        marginTop: 20, padding: 18, borderRadius: 16,
        background: `linear-gradient(180deg, ${RT.magentaTint}, rgba(124,58,237,0.04))`,
        border: `1px solid ${RT.magentaTintBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14.5 }}>or remember everyone.</div>
          <div style={{ color: RT.muted, fontSize: 12, marginTop: 2 }}>
            no choice to make — all {s.candidates.length}{s.truncated ? '+' : ''} stay with f8.
          </div>
        </div>
        <CtaBtn label={`remember everyone · $${monthly.toFixed(2)}/mo`} onClick={onUpgrade} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, margin: '24px 0 12px' }}>
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n} of {lim} remembered</span>
        <span style={{ color: full ? RT.gold : RT.dim, fontSize: 11 }}>
          {full ? `that’s ${lim} — unmark one to swap` : 'nothing is chosen for you'}
        </span>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 9, paddingBottom: 110,
      }}>
        {rows.map((row) => (
          <KeepCard key={row.candidate.personId} row={row} kept={kept.has(row.candidate.personId)} full={full} onToggle={onToggle} />
        ))}
      </div>
      <div style={{
        position: 'sticky', bottom: 0, marginTop: 'auto', padding: '14px 0 28px',
        background: `linear-gradient(180deg, transparent, ${BG} 34%)`,
      }}>
        <button
          onClick={onCommit}
          disabled={!canCommit}
          style={{
            appearance: 'none', cursor: canCommit ? 'pointer' : 'default', width: '100%',
            padding: '14px 20px', borderRadius: 14, fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            background: n > 0 ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${n > 0 ? 'rgba(255,255,255,0.18)' : RT.border}`,
            color: n > 0 ? '#fff' : RT.dim, transition: 'all 0.15s',
          }}
        >
          {committing ? 'remembering…' : n === 0 ? 'tap the ppl f8 remembers' : `remember ${n === 1 ? 'this 1' : `these ${n}`}`}
        </button>
        {/* Only the failure speaks. With nothing to say there is no line at
            all — not a reserved empty one that holds open a blank row. */}
        {commitError && (
          <div style={{ textAlign: 'center', color: RT.gold, fontSize: 10.5, marginTop: 7 }}>
            that didn’t save — nothing changed. try again.
          </div>
        )}
      </div>
    </div>
  );
}

function BeatReassure({ s, kept, committed, windowDays, onExit }: {
  s: TrialSummary; kept: Set<string>; committed: CommitResult | null;
  windowDays: number | null; onExit: () => void;
}) {
  const keptList = s.candidates.filter((p) => kept.has(p.personId));
  // The commit's own transaction read is the authority — never the user-doc
  // counter, which can drift and turn this into "the other -5 are paused".
  const pausedN = Math.max(0, (committed?.rememberedCount ?? s.rememberedCount) - keptList.length);
  const rows = [
    'chat with f8 — still unlimited',
    'contact + calendar sync — still on',
    'circles, rooms, posts — untouched',
    ...(windowDays !== null
      ? [`f8’s working timeline is ${windowDays} days at a time — older memories are archived, in reach the moment you return`]
      : []),
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%', padding: '64px 0 48px' }}>
      <Kicker>done</Kicker>
      <h1 style={{ margin: 0, fontSize: 'clamp(27px, 4.5vw, 38px)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.06, color: '#fff' }}>
        here&rsquo;s where<br />things stand.
      </h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28, maxWidth: 480 }}>
        <div style={{ padding: 16, borderRadius: 16, background: RT.magentaTint, border: `1px solid ${RT.magentaTintBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            {keptList.map((p) => (
              <span key={p.personId} style={{ marginRight: -8 }}><MonoAvatar name={p.name} size={30} /></span>
            ))}
            <span style={{ marginLeft: 14, color: '#fff', fontSize: 13, fontWeight: 600 }}>
              f8 remembers {keptList.length === 1 ? keptList[0].name : `these ${keptList.length}`}
            </span>
          </div>
          <div style={{ color: RT.muted, fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
            briefs, birthdays, where you left off — same as the trial, for {keptList.length === 1 ? 'them' : `your ${keptList.length}`}.
          </div>
        </div>
        {pausedN > 0 && (
          <div style={{ padding: 16, borderRadius: 16, background: RT.goldTint, border: `1px solid ${RT.goldTintBorder}`, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <span style={{
              width: 28, height: 28, borderRadius: 10, background: 'rgba(251,191,36,0.10)',
              border: `1px solid ${RT.goldTintBorder}`, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}>
              <Moon size={13} />
            </span>
            <div>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>the other {pausedN} are paused</div>
              <div style={{ color: RT.muted, fontSize: 12, marginTop: 3, lineHeight: 1.55 }}>
                f8 still has everything it learned about them — paused, not lost. the moment
                you return, it picks every story back up.
              </div>
            </div>
          </div>
        )}
        <div style={{ padding: 16, borderRadius: 16, background: RT.surface, border: `1px solid ${RT.border}` }}>
          <div style={{ color: RT.dim, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 8 }}>
            what didn&rsquo;t change
          </div>
          {rows.map((t) => (
            <div key={t} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: RT.muted, fontSize: 12.5, lineHeight: 1.5, padding: '3px 0' }}>
              <span style={{ flexShrink: 0, marginTop: 2, display: 'inline-flex' }}>
                <CheckIcon size={12} color={RT.magenta2} />
              </span>
              <span>{t}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 28, flexWrap: 'wrap' }}>
        <GhostBtn label="back to f8" onClick={onExit} />
        <span style={{ color: RT.dim, fontSize: 12 }}>premium remembers everyone again — anytime.</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The flow
 * ------------------------------------------------------------------ */

export default function DowngradeTakeover({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<TrialSummary | null>(null);
  const [beat, setBeat] = useState(0);
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState(false);
  const [committed, setCommitted] = useState<CommitResult | null>(null);
  const [monthly, setMonthly] = useState(DEFAULT_USD_MONTHLY);
  const [windowDays, setWindowDays] = useState<number | null>(null);
  const viewLogged = useRef(false);

  useEffect(() => {
    let alive = true;
    // markSeen rides the render-deciding fetch, memoized for the session —
    // a StrictMode double-mount or route remount shares one response instead
    // of reading its own stamp back as "another device got there first".
    fetchTrialSummaryOnce()
      .then((s) => {
        if (!alive) return;
        // `eligible` is the server's word: mid-trial, converted-elsewhere, or
        // bot — whatever this tab's stale snapshot believed, show nothing.
        if (!s.hasTrial || !s.eligible || s.seen || s.freeLimit === null) { onDone(); return; }
        setSummary(s);
        // The escape-hatch price is only spoken in the full variant.
        if (s.candidates.length > s.freeLimit) {
          getPricing().then((p) => { if (alive) setMonthly(p.usdMonthly); }).catch(() => {});
        }
      })
      .catch((err) => {
        // The takeover is a one-shot moment, not an errand: a failed fetch
        // exits to the normal app (the marker is only stamped on a complete
        // response, so the next open retries), never an error wall.
        console.error('trial summary fetch failed:', err);
        if (alive) onDone();
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!summary || viewLogged.current) return;
    viewLogged.current = true;
    capture(MonetizationEvents.DOWNGRADE_FLOW_VIEWED, {
      source: PaywallSource.DOWNGRADE_TAKEOVER,
      eligible: summary.candidates.length,
      free_limit: summary.freeLimit,
      learned: summary.learned,
      prepped: summary.prepped,
    });
  }, [summary]);

  const cardRows = useMemo<CardMeta[]>(() => {
    if (!summary) return [];
    const now = new Date();
    return summary.candidates.map((c) => ({
      candidate: c,
      meta: [
        c.things !== null ? `${c.things} thing${c.things === 1 ? '' : 's'}` : null,
        c.lastAt ? shortDate(c.lastAt) : null,
      ].filter(Boolean).join(' · '),
      birthday: upcomingBirthday(c, now),
    }));
  }, [summary]);

  const toggle = useCallback((id: string) => {
    setKept((prev) => {
      const limit = summary?.freeLimit ?? 0;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < limit) next.add(id);
      return next;
    });
  }, [summary]);

  if (!summary) return null;
  const variant = summary.candidates.length === 0
    ? 'zero'
    : summary.candidates.length <= (summary.freeLimit as number) ? 'few' : 'full';

  const commit = async () => {
    setCommitting(true);
    setCommitError(false);
    try {
      const result = await commitKeepSelection([...kept]);
      setCommitted(result);
      capture(MonetizationEvents.PEOPLE_KEPT_SELECTED, {
        source: PaywallSource.DOWNGRADE_TAKEOVER,
        kept: kept.size,
        eligible: summary.candidates.length,
        free_limit: summary.freeLimit,
      });
      // The reassure beat's window line is live config — served, never a constant.
      fetchRememberQuota()
        .then((q) => setWindowDays(q.freeMemoryWindowDays ?? q.memoryWindowDays))
        .catch(() => {});
      setBeat(2);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code || '';
      // failed-precondition: the choice no longer applies (paid on another
      // device, or the account outgrew one commit). Retrying can only fail
      // the same way — exit to the normal app instead of walling the user.
      if (code.includes('failed-precondition') || code.includes('not-found')) {
        console.warn('keep selection no longer applies:', code);
        onDone();
        return;
      }
      console.error('keep selection commit failed:', err);
      setCommitError(true);
    } finally {
      setCommitting(false);
    }
  };

  const upgrade = () => {
    capture(MonetizationEvents.DOWNGRADE_UPGRADE_TAPPED, {
      source: PaywallSource.DOWNGRADE_TAKEOVER,
      kept_so_far: kept.size,
      eligible: summary.candidates.length,
    });
    onDone();
    navigate(`/premium?source=${PaywallSource.DOWNGRADE_TAKEOVER}`);
  };

  let body: React.ReactNode;
  if (variant === 'zero') {
    body = <BeatZero s={summary} onExit={onDone} onSync={() => { onDone(); navigate('/contacts'); }} />;
  } else if (variant === 'few') {
    body = <BeatFew s={summary} onExit={onDone} />;
  } else if (beat === 0 && summary.learned !== null && summary.people !== null) {
    body = <BeatReceipt s={summary} onNext={() => setBeat(1)} />;
  } else if (beat <= 1) {
    // A null count drops the receipt, never renders a zero nobody measured —
    // the choice still matters, so the flow starts there.
    body = (
      <BeatChoice
        s={summary}
        rows={cardRows}
        monthly={monthly}
        kept={kept}
        onToggle={toggle}
        onCommit={commit}
        onUpgrade={upgrade}
        committing={committing}
        commitError={commitError}
      />
    );
  } else {
    body = <BeatReassure s={summary} kept={kept} committed={committed} windowDays={windowDays} onExit={onDone} />;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Your trial ended"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, overflow: 'auto',
        background: BG, backgroundImage: GLOW, color: '#fff',
      }}
    >
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 32px', minHeight: '100%' }}>
        {body}
      </div>
    </div>
  );
}
